import { useNavigate } from "@tanstack/react-router";
import {
  BadgeCheck,
  CandlestickChart,
  CornerDownLeft,
  Layers,
  Search,
} from "lucide-react";
import { Dialog } from "radix-ui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { useApi } from "@/api/hooks";
import { navItems, pages } from "@/app/registry";
import { useSession } from "@/app/session";
import { cn } from "@/lib/cn";

interface Hit {
  key: string;
  group: string;
  label: string;
  detail?: string;
  icon: ReactNode;
  go: () => void;
}

const HAS_CHAIN = pages.some((p) => p.path === "/app/option-chain");

/**
 * Search for anything: pages, stocks and indices (with their option chain when
 * they trade in F&O) and research analysts. Ctrl/⌘ K or "/" opens it.
 */
export function GlobalSearch() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing =
        e.target instanceof HTMLElement &&
        e.target.closest("input, textarea, [contenteditable]");
      if (
        (e.key === "k" && (e.metaKey || e.ctrlKey)) ||
        (e.key === "/" && !typing)
      ) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger
        className="flex h-9 w-9 items-center gap-2 rounded-lg border border-border bg-surface px-2.5 text-sm text-subtle transition-colors hover:border-border-strong hover:text-muted sm:w-56 lg:w-44 2xl:w-64"
        aria-label="Search for anything"
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="hidden truncate sm:inline">Search for anything</span>
        <kbd className="ml-auto hidden rounded border border-border px-1.5 text-2xs whitespace-nowrap sm:inline lg:hidden 2xl:inline">
          Ctrl K
        </kbd>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          className="fixed top-[10vh] left-1/2 z-50 w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-2xl"
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">Search</Dialog.Title>
          {open ? <Panel close={() => setOpen(false)} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Panel({ close }: { close: () => void }) {
  const navigate = useNavigate();
  const { user } = useSession();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const list = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 150);
    return () => clearTimeout(t);
  }, [text]);

  const stocks = useApi(
    "/stocks/search",
    { query: { q, limit: 8 } },
    { enabled: q.length > 0 },
  );
  const ras = useApi(
    "/directory/ras",
    { query: { q, page_size: 5 } },
    // an RA's client doesn't look for other analysts
    { enabled: q.length > 1 && user?.ra_client !== true },
  );

  const hits = useMemo<Hit[]>(() => {
    const go = (fn: () => Promise<unknown>) => () => {
      close();
      void fn();
    };
    const needle = q.toLowerCase();
    const out: Hit[] = [];
    if (needle) {
      for (const p of navItems(
        pages,
        user?.role,
        user?.ai_calls_access === true,
        user?.access_tier ?? "full",
        user?.ra_client === true,
      )) {
        if (p.nav.label.toLowerCase().includes(needle)) {
          const Icon = p.nav.icon;
          out.push({
            key: `p${p.path}`,
            group: "Pages",
            label: p.nav.label,
            icon: <Icon className="size-4" />,
            go: go(() => navigate({ to: p.path })),
          });
        }
      }
    }
    for (const s of stocks.data ?? []) {
      out.push({
        key: `s${s.symbol}`,
        group: "Stocks and indices",
        label: s.symbol,
        detail: [
          s.name,
          s.kind === "index" ? "Index" : null,
          s.fno ? "F&O" : null,
        ]
          .filter(Boolean)
          .join(" · "),
        icon: <CandlestickChart className="size-4" />,
        go: go(() =>
          navigate({ to: "/app/stocks/$symbol", params: { symbol: s.symbol } }),
        ),
      });
      if (s.fno && HAS_CHAIN) {
        out.push({
          key: `c${s.symbol}`,
          group: "Stocks and indices",
          label: `${s.symbol} option chain`,
          detail: "Live calls and puts by strike",
          icon: <Layers className="size-4" />,
          go: go(() =>
            navigate({
              to: "/app/option-chain",
              search: { underlying: s.symbol } as never,
            }),
          ),
        });
      }
    }
    for (const r of ras.data?.results ?? []) {
      out.push({
        key: `r${r.reg_no}`,
        group: "Research analysts",
        label: r.name,
        detail: [r.reg_no, r.city].filter(Boolean).join(" · "),
        icon: <BadgeCheck className="size-4" />,
        go: go(() =>
          navigate({
            to: "/find-research-analyst/$regNo",
            params: { regNo: r.reg_no },
          }),
        ),
      });
    }
    return out;
  }, [q, stocks.data, ras.data, user, navigate, close]);

  useEffect(() => {
    list.current
      ?.querySelector(`[data-index="${at}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [at]);

  const loading = q.length > 0 && (stocks.isFetching || ras.isFetching);
  let lastGroup = "";
  return (
    <div
      onKeyDown={(e) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setAt((n) => Math.min(n + 1, hits.length - 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setAt((n) => Math.max(n - 1, 0));
        } else if (e.key === "Enter" && hits[at]) {
          e.preventDefault();
          hits[at].go();
        }
      }}
    >
      <div className="flex items-center gap-2 border-b border-border px-4">
        <Search className="size-4 shrink-0 text-subtle" aria-hidden />
        <input
          autoFocus
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setAt(0);
          }}
          placeholder="Stock, index, F&O, analyst or page…"
          aria-label="Search"
          className="h-12 min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-subtle"
        />
        {loading ? (
          <span className="text-2xs text-subtle">searching…</span>
        ) : null}
      </div>
      <ul
        ref={list}
        className="max-h-[60vh] overflow-y-auto p-1.5"
        role="listbox"
        aria-label="Results"
      >
        {q === "" ? (
          <li className="px-3 py-6 text-center text-sm text-subtle">
            Type a symbol, a company, an analyst's name or a page
          </li>
        ) : hits.length === 0 && !loading ? (
          <li className="px-3 py-6 text-center text-sm text-subtle">
            Nothing matches “{q}”
          </li>
        ) : (
          hits.map((h, n) => {
            const header = h.group !== lastGroup ? h.group : null;
            lastGroup = h.group;
            return (
              <li key={h.key} role="presentation">
                {header ? (
                  <div className="px-2.5 pt-2 pb-1 text-2xs font-semibold tracking-wider text-subtle uppercase">
                    {header}
                  </div>
                ) : null}
                <button
                  type="button"
                  role="option"
                  aria-selected={n === at}
                  data-index={n}
                  onMouseMove={() => setAt(n)}
                  onClick={h.go}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm",
                    n === at ? "bg-surface-2 text-fg" : "text-fg",
                  )}
                >
                  <span className="text-muted">{h.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {h.label}
                    </span>
                    {h.detail ? (
                      <span className="block truncate text-xs text-muted">
                        {h.detail}
                      </span>
                    ) : null}
                  </span>
                  {n === at ? (
                    <CornerDownLeft
                      className="size-3.5 text-subtle"
                      aria-hidden
                    />
                  ) : null}
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
